import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  listStorefrontNewsletterSubscribersForAdmin,
  suppressStorefrontNewsletterSubscriberForAdmin,
  type ListStorefrontNewsletterSubscribersForAdminInput,
  type SuppressStorefrontNewsletterSubscriberForAdminInput,
} from '../services/storefront-newsletter-subscribers-admin.service';
import type { SupabaseClient } from '../supabase/client';
import type { StorefrontNewsletterSubscriberAdminRow } from '../types/storefront-engine';

export const storefrontNewsletterSubscribersAdminKeys = {
  all: ['storefront-newsletter-subscribers-admin'] as const,
  lists: (storeId: string) => ['storefront-newsletter-subscribers-admin', 'list', storeId] as const,
  list: (storeId: string, limit: number) =>
    ['storefront-newsletter-subscribers-admin', 'list', storeId, limit] as const,
};

export function useStorefrontNewsletterSubscribersForAdmin(
  client: SupabaseClient,
  input: {
    readonly storeId: string | null;
    readonly limit?: number;
  },
): UseQueryResult<StorefrontNewsletterSubscriberAdminRow[]> {
  const storeId = input.storeId?.trim() ?? '';
  const limit = input.limit ?? 50;
  return useQuery({
    queryKey: storeId
      ? storefrontNewsletterSubscribersAdminKeys.list(storeId, limit)
      : storefrontNewsletterSubscribersAdminKeys.list('pending', limit),
    enabled: storeId !== '',
    queryFn: () =>
      listStorefrontNewsletterSubscribersForAdmin(client, {
        storeId,
        limit,
      }),
    staleTime: 30_000,
  });
}

export function useSuppressStorefrontNewsletterSubscriberForAdmin(
  client: SupabaseClient,
): UseMutationResult<
  StorefrontNewsletterSubscriberAdminRow,
  unknown,
  SuppressStorefrontNewsletterSubscriberForAdminInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => suppressStorefrontNewsletterSubscriberForAdmin(client, input),
    onMutate: (input) =>
      queryClient.getQueryCache().findAll({
        queryKey: storefrontNewsletterSubscribersAdminKeys.lists(input.storeId),
      }),
    onSuccess: (_subscriber, _input, originatingQueries) => {
      // Scope removal can replace a Query at the same store/limit key.
      // A late RPC may only invalidate the genuine objects captured before it.
      const survivingQueries = queryClient
        .getQueryCache()
        .getAll()
        .filter((query) => originatingQueries?.includes(query));
      if (survivingQueries.length === 0) return;
      void queryClient.invalidateQueries({
        predicate: (query) => survivingQueries.includes(query),
      });
    },
  });
}

export type { ListStorefrontNewsletterSubscribersForAdminInput };
